import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditCopyErrorMessage } from './credit-copy-errors';
import {
  CreditNoteFormModal,
  type CreditNoteFollowUp,
  type CreditNoteLineSelection,
} from './CreditNoteFormModal';
import { ConfirmDialog } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { CREDIT_NOTE_NOT_SENT_NOTE, creditNotePushOutcome } from '@/lib/credit-notes';
import { documentDisplayTitle } from '@/lib/format';
import type { CreditNote, Invoice } from '@/lib/types';

/* -------------------------------------------------------------------------
 * Raising a credit note FROM an invoice — the whole flow, in one place.
 *
 * ## Why this exists
 *
 * It was written twice: once in `InvoicesPage` and once in
 * `InvoiceDetailPage`, near-identically, and both copies carried a comment
 * saying so — *"This is a second copy of the same flow … it belongs with the
 * gate, in one module."* This is that.
 *
 * ⚠️ **The duplication was not a tidiness problem.** What the two copies decide
 * between them is **which lines of a customer's invoice get handed back as
 * money**, and the two surfaces are one click apart — a list row and the record
 * that row opens. Two copies of that would agree on the day they were written
 * and drift on the first correction, and the drift would be invisible: both
 * screens would go on returning a `201`.
 *
 * ## The shape, and why it is a hook rather than a component
 *
 * The TRIGGER lives deep in a tree — a button on a table row, on a card, in a
 * header — while the DIALOGS have to sit at page level. A component taking an
 * `invoice` prop cannot express that split without the page re-inventing the
 * state it was meant to be relieved of, so the hook hands back three things:
 * `start` for the trigger, `checkingId` for the trigger's busy state, and
 * `dialogs` to render once.
 *
 * ## ⚠️ IT IS TWO REQUESTS AND THERE IS NO ROLLBACK
 *
 * `POST /credit-notes` creates the note; `POST /credit-notes/:id/products/from-invoice`
 * copies the lines. A line cannot be copied onto a note that does not exist
 * yet, so no arrangement of this UI makes it one write — and there is no
 * compensating endpoint that would be safe to fire automatically.
 *
 * ⛔ **So a failed copy is reported as a PARTIAL and never as a failed create.**
 * Telling somebody "nothing was saved" after a successful `POST /credit-notes`
 * is a lie they ACT on: they close the dialog and raise a second credit note
 * against a customer, and now there are two.
 *
 * ## Phase three: "Save and submit" / "Save and approve" (owner 2026-10-28)
 *
 * The form may hand back a follow-up, run by `finish` only AFTER the lines
 * copied (approve allocates to the source invoice). A failed copy skips it; a
 * refused follow-up leaves the note Draft and is reported, never as a failed
 * create. Neither emails the customer (`CREDIT_NOTE_NOT_SENT_NOTE`).
 * --------------------------------------------------------------------- */

/** The already-credited check, held between the trigger and the form. */
interface Precheck {
  invoice: Invoice;
  /**
   * How many credit notes already name this invoice as their source.
   *
   * ⚠️ **`null` is NOT zero — it is *we could not ask*.** The two lead to
   * different sentences: zero means this is the first credit against the
   * invoice, and `null` means there may already be one and nobody knows. Folding
   * them would let somebody raise a duplicate credit believing they had been
   * told there was none.
   */
  count: number | null;
}

/** The note exists and its lines did not copy. */
interface Partial {
  note: CreditNote;
  detail: string;
  /** The follow-up that was asked for and therefore NOT run. */
  followUp?: CreditNoteFollowUp;
}

/**
 * The note and its lines exist, and what came after did not fully land
 * (owner 2026-10-28): the submit or approve was refused (`kind: 'refused'`,
 * the note stays Draft), or the approval stands and Xero did not accept it
 * (`kind: 'xero'`).
 */
interface FollowUpReport {
  note: CreditNote;
  followUp: CreditNoteFollowUp;
  kind: 'refused' | 'xero';
  detail: string;
}

/** The sentence for a refused follow-up. Specific before generic. */
function followUpErrorMessage(caught: unknown): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return 'Could not reach the server. Check your connection and try again.';
}

export interface RaiseCreditNote {
  /**
   * Begin. Runs the already-credited precheck, then opens the form — or the
   * confirmation first, where something has been credited already.
   */
  start: (invoice: Invoice) => void;
  /** The invoice whose precheck is in flight, for the trigger's busy state. */
  checkingId: number | null;
  /** Mount ONCE, at page level. */
  dialogs: ReactNode;
}

export function useRaiseCreditNote(): RaiseCreditNote {
  const navigate = useNavigate();
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [precheck, setPrecheck] = useState<Precheck | null>(null);
  const [formFor, setFormFor] = useState<Invoice | null>(null);
  const [partial, setPartial] = useState<Partial | null>(null);
  const [followUpReport, setFollowUpReport] = useState<FollowUpReport | null>(null);

  /**
   * ⚠️ **`?sourceInvoiceId=` AND `?allocatedToInvoiceId=` ARE ONE WORD APART AND
   * ANSWER DIFFERENT QUESTIONS.** This one asks what has been credited FROM this
   * invoice; the other asks what a credit was SPENT ON. The wrong key answers
   * `200` with a plausible count about an unrelated relationship, and the
   * sentence built from it would be confidently wrong.
   *
   * ⚠️ **It WARNS and never REFUSES.** A second credit note against one invoice
   * is legitimate — a partial credit followed by another — so this names what is
   * already there and lets a person decide.
   */
  async function start(invoice: Invoice) {
    setCheckingId(invoice.id);
    try {
      const existing = await api.listCreditNotes({ sourceInvoiceId: invoice.id, perPage: 1 });
      const count = existing.meta.total;
      /*
       * Nothing credited yet is the ordinary case and gets no dialog — a
       * confirmation that always appears is one nobody reads by Friday.
       */
      if (count === 0) setFormFor(invoice);
      else setPrecheck({ invoice, count });
    } catch {
      setPrecheck({ invoice, count: null });
    } finally {
      setCheckingId((current) => (current === invoice.id ? null : current));
    }
  }

  /**
   * Phase two. **The note already exists by the time this runs.**
   *
   * 🔴 **THE SELECTION DECIDES WHETHER `invoiceProductIds` IS SENT AT ALL, and
   * the two branches mean opposite things.** An absent key credits EVERY line on
   * the invoice; `[]` is a **422**. So the key is built from a named union
   * rather than from an array that might be empty — see
   * {@link CreditNoteLineSelection}, where the reasoning lives.
   *
   * ⚠️ **`undefined` means copy NOTHING**, which is a real outcome: an invoice
   * with no lines, or a note raised from no invoice at all. It must not fall
   * through to the omit-the-key branch, because that branch is a request.
   */
  async function copyLines(
    invoice: Invoice,
    note: CreditNote,
    lines?: CreditNoteLineSelection,
    followUp?: CreditNoteFollowUp,
  ) {
    if (lines === undefined) {
      await finish(note, followUp);
      return;
    }
    /*
     * ⚠️ **The cities were chosen on the form, beside the ticks** (owner
     * 2026-10-27), so this is ONE request: `cities` names only the lines
     * credited for some of their cities, and is omitted when none is narrowed.
     * A refused city (409 already credited, 422 not on the line) refuses the
     * whole copy — one transaction — and lands in the partial report below,
     * worded by `creditCopyErrorMessage`.
     */
    try {
      await api.copyInvoiceLinesToCreditNote(note.id, {
        invoiceId: invoice.id,
        ...(lines.kind === 'ids'
          ? {
              invoiceProductIds: lines.invoiceProductIds,
              ...(lines.cities.length > 0 ? { cities: lines.cities } : {}),
            }
          : {}),
      });
    } catch (caught) {
      /*
       * ⚠️ **The follow-up is NOT run over a failed copy.** Approving would
       * allocate an empty note and submitting would send one to the approvers;
       * the report says it did not happen.
       */
      setPartial({
        note,
        detail: creditCopyErrorMessage(caught),
        ...(followUp === undefined ? {} : { followUp }),
      });
      return;
    }
    await finish(note, followUp);
  }

  /**
   * Phase three (owner 2026-10-28): the submit or approve the form asked for,
   * once the lines exist. **The note is saved whatever this says** — a refusal
   * leaves it Draft and is reported as such, never as a failed create.
   */
  async function finish(note: CreditNote, followUp?: CreditNoteFollowUp) {
    if (followUp === undefined) {
      navigate(`/credit-notes/${note.id}`);
      return;
    }
    try {
      if (followUp === 'submit') {
        await api.submitCreditNoteForApproval(note.id);
      } else {
        const response = await api.approveCreditNote(note.id);
        /* `?.` — an older server answers `{data}` alone. The approval stands. */
        if (response.xeroPush?.status === 'failed') {
          setFollowUpReport({
            note,
            followUp,
            kind: 'xero',
            detail: creditNotePushOutcome(response.xeroPush, 'approve').text,
          });
          return;
        }
      }
      navigate(`/credit-notes/${note.id}`);
    } catch (caught) {
      setFollowUpReport({ note, followUp, kind: 'refused', detail: followUpErrorMessage(caught) });
    }
  }

  const dialogs = (
    <>
      {/*
        The already-credited warning. ⚠️ Its own two branches: a real count, and
        a check that did not come back. Only the first may name a number.

        🔴 **THE TITLE NAMES THE INVOICE, which is where the two copies had
        ALREADY DRIFTED.** `InvoicesPage` built it with `documentDisplayTitle`;
        `InvoiceDetailPage` carried a flat "Raise another credit note?". Both
        read fine in place — on a detail page the record is on screen behind the
        dialog — but they are two answers to one question, and the naming one is
        the answer that survives being moved. **The stronger of the two survives
        the merge**, the same ruling `financial-shared.ts` records for retiring a
        duplicate.

        ⚠️ **So the list page's wording is now what the DETAIL page shows too.**
        That is a visible change on one surface, and it is the point rather than
        a side effect: the drift was invisible precisely because nobody sees
        both screens at once.
      */}
      <ConfirmDialog
        open={precheck !== null}
        onClose={() => setPrecheck(null)}
        onConfirm={() => {
          const invoice = precheck?.invoice ?? null;
          setPrecheck(null);
          if (invoice) setFormFor(invoice);
        }}
        tone="primary"
        title={
          precheck
            ? `Raise another credit note for ${documentDisplayTitle(
                precheck.invoice.subject,
                precheck.invoice.xeroInvoiceNumber,
              )}?`
            : 'Raise a credit note?'
        }
        description={
          precheck === null
            ? ''
            : precheck.count === null
              ? 'We couldn’t check whether this invoice has already been credited. There may be a credit note against it already.'
              : `${precheck.count} credit ${
                  precheck.count === 1 ? 'note has' : 'notes have'
                } already been raised from this invoice.`
        }
        confirmLabel="Raise credit note"
      />

      {/*
        🔴 **THE COMPANY AND CURRENCY ARE SEEDED AND NEITHER CAN BE CHANGED
        AFTERWARDS** — `CreditNoteUpdateInput` omits both, since
        `(companyId, currency)` is the identity a credit BALANCE is struck at.
        Seeding them from the invoice is what makes the note credit the account
        that was billed.

        ⚠️ **The credit REASON is deliberately NOT seeded.** The column is
        `NOT NULL` and there is no unreasoned credit note, so a person chooses
        one; a default id invented here would file every credit under a reason
        nobody picked, on the field an auditor reads.

        ✅ **`sourceInvoice` is what puts the LINE PICKER on the form.** Without
        it the form has nothing to pick over and renders no section — which is
        the complimentary-credit case, raised from no invoice at all.
      */}
      {formFor && formFor.company && (
        <CreditNoteFormModal
          open
          lockedCompany={{ id: formFor.company.id, name: formFor.company.name }}
          sourceInvoice={{ id: formFor.id, currency: formFor.currency }}
          prefill={{
            ...(formFor.currency === null ? {} : { currency: formFor.currency }),
            subject: `Credit note for ${documentDisplayTitle(
              formFor.subject,
              formFor.xeroInvoiceNumber,
            )}`,
          }}
          onClose={() => setFormFor(null)}
          onSaved={(
            note: CreditNote,
            lines?: CreditNoteLineSelection,
            followUp?: CreditNoteFollowUp,
          ) => {
            const invoice = formFor;
            setFormFor(null);
            void copyLines(invoice, note, lines, followUp);
          }}
        />
      )}

      {/*
        🔴 **THE PARTIAL REPORT — the note EXISTS and its lines do not.** This
        sentence is the whole reason the flow does not report a failed create,
        and it is the wording that decides whether somebody raises a duplicate
        credit against a customer. The only useful next step is to open the note
        and see what is on it; dismissing leaves the reader where they were,
        which is also legitimate.

        ⚠️ **`tone="primary"`, NOT the destructive default.** Nothing here is
        about to be destroyed — a record was created and a follow-up did not
        land — and a red dialog over a successful create reads as the create
        having failed, which is the one conclusion this dialog exists to
        prevent. **Both copies this replaces already agreed on that**, and on
        the cancel label; this one is carried across unchanged.
      */}
      <ConfirmDialog
        open={partial !== null}
        onClose={() => setPartial(null)}
        onConfirm={() => {
          const noteId = partial?.note.id ?? null;
          setPartial(null);
          if (noteId !== null) navigate(`/credit-notes/${noteId}`);
        }}
        tone="primary"
        title="The credit note was created, its lines were not copied"
        description={
          partial === null
            ? ''
            : (partial.detail === ''
                ? `${partial.note.subject} exists and is empty — no lines were copied from this invoice.`
                : `${partial.note.subject} exists and is empty — the line items could not be copied from this invoice. ${partial.detail}`) +
              (partial.followUp === 'approve'
                ? ' It was not approved and stays a Draft.'
                : partial.followUp === 'submit'
                  ? ' It was not submitted for approval and stays a Draft.'
                  : '')
        }
        confirmLabel="Open credit note"
        cancelLabel="Stay here"
      />

      {/*
        The follow-up report (owner 2026-10-28). ⚠️ `tone="primary"` for the
        reason on the partial report above: the note was created, and a red
        dialog would read as the create having failed.
      */}
      <ConfirmDialog
        open={followUpReport !== null}
        onClose={() => setFollowUpReport(null)}
        onConfirm={() => {
          const noteId = followUpReport?.note.id ?? null;
          setFollowUpReport(null);
          if (noteId !== null) navigate(`/credit-notes/${noteId}`);
        }}
        tone="primary"
        title={
          followUpReport === null
            ? 'The credit note was created'
            : followUpReport.kind === 'xero'
              ? 'The credit note was created and approved, Xero did not accept it'
              : followUpReport.followUp === 'approve'
                ? 'The credit note was created, it was not approved'
                : 'The credit note was created, it was not submitted for approval'
        }
        description={
          followUpReport === null
            ? ''
            : followUpReport.kind === 'xero'
              ? `${followUpReport.detail} ${CREDIT_NOTE_NOT_SENT_NOTE}`
              : `${followUpReport.note.subject} was saved and stays a Draft. ${followUpReport.detail}`
        }
        confirmLabel="Open credit note"
        cancelLabel="Stay here"
      />
    </>
  );

  return { start, checkingId, dialogs };
}
